import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const QuickTripForm = ({ 
  formData, 
  handleChange, 
  handleSubmit, 
  onBack, 
  isSubmitting,
  destinations,
  handleAddDestination,
  handleRemoveDestination,
  handleUpdateDestination,
  handlePrimaryTravelModeChange,
  handleSecondaryTravelModeToggle
}) => {
  return (
    <div className="create-packit-container">
      <h1>Quick Trip</h1>
      <form className="create-packit-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3 className="section-title">Basic Information</h3>
          
          <label>
            Trip Name
            <input
              type="text"
              name="tripName"
              value={formData.tripName}
              onChange={handleChange}
              placeholder="Enter trip name"
              required
            />
          </label>
          
          <div className="horizontal-group">
            <div>
              <label>Start Date</label>
              <DatePicker
                selected={formData.startDate}
                onChange={(date) => {
                  const newFormData = { ...formData, startDate: date };
                  // Clear end date if it's before the new start date
                  if (date && formData.endDate && formData.endDate < date) {
                    newFormData.endDate = null;
                    handleChange({ target: { name: 'endDate', value: null } });
                  }
                  handleChange({ target: { name: 'startDate', value: date } });
                }}
                placeholderText="Select start date"
                required
                popperPlacement="top-start"
                showFixedNumberOfWeeks={true}
                fixedHeight={true}
              />
            </div>
            <div>
              <label>End Date</label>
              <DatePicker
                selected={formData.endDate}
                onChange={(date) => handleChange({ target: { name: 'endDate', value: date } })}
                placeholderText="Select end date"
                required
                minDate={formData.startDate}
                popperPlacement="top-start"
                showFixedNumberOfWeeks={true}
                fixedHeight={true}
                openToDate={formData.startDate || new Date()}
              />
            </div>
          </div>
        </div>

        {/* Travel Modes Section */}
        <div className="form-section">
          <h3 className="section-title">Travel Modes</h3>
          <p className="section-description">
            Select your primary mode of travel to reach your destination, and any secondary modes you'll use during your trip.
          </p>
          
          <div className="travel-modes-container">
            <div className="primary-travel-mode">
              <h4 className="subsection-title">Primary Mode of Travel</h4>
              <p className="subsection-description">How will you primarily get to your destination?</p>
              <div className="travel-mode-options">
                {[
                  { value: 'plane', label: '✈️ Flight' },
                  { value: 'driving', label: '🚗 Driving' },
                  { value: 'train', label: '🚂 Train' },
                  { value: 'bus', label: '🚌 Bus' },
                  { value: 'ferry', label: '🚢 Ferry' },
                  { value: 'walking', label: '🚶 Walking' },
                  { value: 'public_transit', label: '🚇 Public Transit' },
                  { value: 'rideshare', label: '🚕 Rides' }
                ].map((mode) => (
                  <button
                    type="button"
                    key={mode.value}
                    className={`travel-mode-button ${formData.primaryTravelMode === mode.value ? 'selected' : ''}`}
                    onClick={() => handlePrimaryTravelModeChange(mode.value)}
                  >
                    <span className="travel-mode-icon">{mode.label.split(' ')[0]}</span>
                    <div className="travel-mode-details">
                      <span className="travel-mode-label">{mode.label.replace(/^[^\s]+\s/, '')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="secondary-travel-modes">
              <h4 className="subsection-title">Secondary Modes of Travel</h4>
              <p className="subsection-description">What other transportation will you use during your trip?</p>
              <div className="travel-mode-options">
                {[
                  { value: 'plane', label: '✈️ Flight' },
                  { value: 'driving', label: '🚗 Driving' },
                  { value: 'train', label: '🚂 Train' },
                  { value: 'bus', label: '🚌 Bus' },
                  { value: 'ferry', label: '🚢 Ferry' },
                  { value: 'walking', label: '🚶 Walking' },
                  { value: 'public_transit', label: '🚇 Public Transit' },
                  { value: 'rideshare', label: '🚕 Rides' }
                ].map((mode) => (
                  <button
                    type="button"
                    key={mode.value}
                    className={`travel-mode-button secondary ${formData.secondaryTravelModes && formData.secondaryTravelModes.includes(mode.value) ? 'selected' : ''}`}
                    onClick={() => handleSecondaryTravelModeToggle(mode.value)}
                  >
                    <span className="travel-mode-icon">{mode.label.split(' ')[0]}</span>
                    <div className="travel-mode-details">
                      <span className="travel-mode-label">{mode.label.replace(/^[^\s]+\s/, '')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Destinations Section */}
        <div className="form-section">
          <h3 className="section-title">Destinations</h3>
          <p className="section-description">
            You can add additional cities or addresses to build your itinerary and trip while specifying the desired number of days at each location.
          </p>
          
          <div className="destinations-container">
            {destinations.map((destination) => (
              <div key={destination.id} className="destination-row">
                <div className="destination-inputs">
                  <input
                    type="text"
                    placeholder="Enter destination"
                    value={destination.name}
                    onChange={(e) =>
                      handleUpdateDestination(destination.id, 'name', e.target.value)
                    }
                    className="destination-name-input"
                    required
                  />
                  <label className="checkbox-label"><span>Days to visit:</span></label>
                  
                  <input
                    type="number"
                    min="0"
                    value={destination.minimumNumberOfDesiredDaysAtLocation}
                    onChange={(e) =>
                      handleUpdateDestination(destination.id, 'minimumNumberOfDesiredDaysAtLocation', Number(e.target.value))
                    }
                    className="destination-days-input"
                    placeholder="Days"
                  />
                </div>
                
                <div className="destination-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={destination.isStart}
                      onChange={(e) =>
                        handleUpdateDestination(destination.id, 'isStart', e.target.checked)
                      }
                    />
                    <span>Is Start?</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={destination.isEnd}
                      onChange={(e) =>
                        handleUpdateDestination(destination.id, 'isEnd', e.target.checked)
                      }
                    />
                    <span>Is End?</span>
                  </label>
                </div>
                
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => handleRemoveDestination(destination.id)}
                  disabled={destinations.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
            
            <button
              type="button"
              className="add-destination-button"
              onClick={handleAddDestination}
            >
               Add Destination
            </button>
          </div>
        </div>

        <div className="button-group">
          <button
            type="button"
            className="create-note-button cancel"
            onClick={onBack}
          >
            Back
          </button>
          <button 
            type="submit" 
            className="create-note-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Quick Trip'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuickTripForm;
